var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/list-config.ts
function defineListPage(config) {
  return config;
}
__name(defineListPage, "defineListPage");

// src/use-list-page.ts
import { useCallback } from "react";

// src/use-paginated-query.ts
import { useQuery } from "@tanstack/react-query";
function usePaginatedQuery(resourceKey, fetchFn, params, options) {
  const { data: raw, isLoading, isFetching, isError, error, refetch } = useQuery({
    placeholderData: /* @__PURE__ */ __name((previousData) => previousData, "placeholderData"),
    ...options,
    queryKey: [
      ...resourceKey,
      "list",
      params
    ],
    queryFn: /* @__PURE__ */ __name(() => fetchFn(params), "queryFn")
  });
  return {
    data: raw?.data,
    meta: raw?.meta,
    isLoading: isLoading && !raw,
    isFetching,
    isError,
    error,
    refetch
  };
}
__name(usePaginatedQuery, "usePaginatedQuery");

// src/use-list-page.ts
function parsePage(value) {
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 ? n : 1;
}
__name(parsePage, "parsePage");
function parseLimit(value, fallback) {
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 && n <= 100 ? n : fallback;
}
__name(parseLimit, "parseLimit");
function parseSort(value) {
  if (!value) return void 0;
  const idx = value.lastIndexOf("_");
  if (idx === -1) return void 0;
  const dir = value.slice(idx + 1);
  if (dir !== "asc" && dir !== "desc") return void 0;
  return {
    field: value.slice(0, idx),
    dir
  };
}
__name(parseSort, "parseSort");
function encodeSort(sort) {
  return `${sort.field}_${sort.dir}`;
}
__name(encodeSort, "encodeSort");
function deriveState(config, urlParams) {
  const limit = parseLimit(urlParams.limit, config.defaultPageSize ?? 20);
  return {
    page: parsePage(urlParams.page),
    limit,
    search: urlParams.search ?? "",
    sort: parseSort(urlParams.sort) ?? config.defaultSort ?? null,
    filters: Object.fromEntries((config.filters ?? []).map((f) => [
      f.id,
      urlParams[f.id] ?? ""
    ]))
  };
}
__name(deriveState, "deriveState");
function toApiParams(config, state) {
  const params = {
    page: String(state.page),
    limit: String(state.limit)
  };
  if (state.search) params.search = state.search;
  if (state.sort) params.sort = encodeSort(state.sort);
  for (const [key, value] of Object.entries(state.filters)) {
    if (value) params[key] = value;
  }
  return params;
}
__name(toApiParams, "toApiParams");
function useListPage(config, fetchFn, urlAdapter) {
  const urlParams = urlAdapter.readParams();
  const state = deriveState(config, urlParams);
  const apiParams = toApiParams(config, state);
  const query = usePaginatedQuery([
    config.resource
  ], fetchFn, apiParams);
  const setPage = useCallback((page) => {
    const current = urlAdapter.readParams();
    urlAdapter.writeParams({
      ...current,
      page: String(page)
    });
  }, [
    urlAdapter
  ]);
  const setSearch = useCallback((search) => {
    const current = urlAdapter.readParams();
    const next = {
      ...current,
      page: "1"
    };
    if (search) {
      next.search = search;
    } else {
      delete next.search;
    }
    urlAdapter.writeParams(next);
  }, [
    urlAdapter
  ]);
  const setSort = useCallback((sort) => {
    const current = urlAdapter.readParams();
    const next = {
      ...current,
      page: "1"
    };
    if (sort) {
      next.sort = `${sort.field}_${sort.dir}`;
    } else {
      delete next.sort;
    }
    urlAdapter.writeParams(next);
  }, [
    urlAdapter
  ]);
  const setFilter = useCallback((key, value) => {
    const current = urlAdapter.readParams();
    const next = {
      ...current,
      page: "1"
    };
    if (value) {
      next[key] = value;
    } else {
      delete next[key];
    }
    urlAdapter.writeParams(next);
  }, [
    urlAdapter
  ]);
  const resetFilters = useCallback(() => {
    const config_ = config;
    const current = urlAdapter.readParams();
    const next = {
      ...current,
      page: "1"
    };
    for (const f of config_.filters ?? []) delete next[f.id];
    delete next.search;
    urlAdapter.writeParams(next);
  }, [
    urlAdapter,
    config
  ]);
  const resetAll = useCallback(() => {
    urlAdapter.writeParams({
      page: "1"
    });
  }, [
    urlAdapter
  ]);
  return {
    data: query.data,
    meta: query.meta,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    page: state.page,
    setPage,
    search: state.search,
    setSearch,
    sort: state.sort,
    setSort,
    filters: state.filters,
    setFilter,
    resetFilters,
    resetAll
  };
}
__name(useListPage, "useListPage");
export {
  defineListPage,
  deriveState,
  toApiParams,
  useListPage,
  usePaginatedQuery
};
//# sourceMappingURL=index.js.map